import React from 'react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { Globe, Copy, ExternalLink } from 'lucide-react'
import { useTranslation } from '@/hooks/useTranslation'

const copyToClipboard = async (text: string) => {
	try {
		await navigator.clipboard.writeText(text)
		return true
	} catch {
		return false
	}
}

interface PublicUrlDisplayProps {
	url: string
}

export const PublicUrlDisplay: React.FC<PublicUrlDisplayProps> = ({ url }) => {
	const { t } = useTranslation()
	const { toast } = useToast()

	const handleCopyUrl = async () => {
		const success = await copyToClipboard(url)
		toast({
			title: success ? t('publicUrlDisplay.urlCopied') : t('publicUrlDisplay.failedToCopyUrl'),
			description: success ? t('publicUrlDisplay.urlCopiedDescription') : t('publicUrlDisplay.pleaseTryCopyingManually'),
			variant: success ? 'default' : 'destructive',
		})
	}

	return (
		<div className="space-y-2">
			<div className="flex items-center gap-2">
				<Globe className="h-4 w-4 text-muted-foreground" />
				<span className="text-sm font-medium">{t('publicUrlDisplay.publicUrl')}</span>
			</div>
			
			<div className="flex items-center gap-2 p-2 border rounded">
				<code className="flex-1 font-mono text-sm text-muted-foreground break-all select-all">
					{url}
				</code>
				<Button
					variant="ghost"
					size="sm"
					onClick={handleCopyUrl}
					className="h-7 px-2">
					<Copy className="h-3 w-3" />
				</Button>
				<Button 
					variant="ghost" 
					size="sm" 
					asChild 
					className="h-7 px-2">
					<a href={url} target="_blank" rel="noopener noreferrer">
						<ExternalLink className="h-3 w-3" />
					</a>
				</Button>
			</div>
		</div>
	)
}
